// ============================================================
// Orymia Shared — Object Effect Presets
// Named object-level lighting/particle recipes (product language)
// that the Space Compiler expands into concrete ObjectEffects.
// ============================================================

import type { ObjectEffects, RimGlowConfig } from "./scene";

/** How a preset's light distance relates to object scale.
 *  - "local" (default): distance is absolute world units, unaffected by scale.
 *    Suitable for small objects (candles, lanterns, campfires).
 *  - "surface": distance scales proportionally with object scale so light
 *    reaches beyond the mesh surface. Suitable for large radiant objects
 *    (moons, crystals, stars). Formula: effectiveDistance = distance × scale × distanceFactor. */
export type EffectScaleMode = "local" | "surface";

/** Extended preset definition — ObjectEffects + compilation metadata.
 *  The extra fields guide the objectEffectsCompiler but are NOT emitted
 *  into the SceneDescriptor (they are stripped during compilation). */
export interface ObjectEffectPresetDef extends ObjectEffects {
  /** Scale behavior hint for the compiler. Default: "local". */
  scaleMode?: EffectScaleMode;
  /** Multiplier applied when scaleMode is "surface": distance × scale × distanceFactor.
   *  Default: 1. Use >1 to push light further beyond the surface. */
  distanceFactor?: number;
  /** Emissive color applied to the object's mesh material (makes the surface glow).
   *  Essential for surface-mode presets — a point light inside a closed mesh
   *  cannot illuminate its own surface; emissive material is required. */
  emissive?: string;
  /** Emissive intensity multiplier. Default: 1. */
  emissiveIntensity?: number;
  /** Fresnel rim glow shell — brightest at silhouette edges, fades toward center.
   *  Simulates light passing through a thin shell (limb glow / subsurface scatter).
   *  Gives shape definition against dark backgrounds. */
  rimGlow?: RimGlowConfig;
}

/** Semantic object-effect presets — authoring shorthand stored in
 *  SpaceObjectEffects.preset and expanded by the Space Compiler into concrete
 *  ObjectEffects (lights + emitters).
 *
 *  Mirrors the CONDITIONS_PRESETS pattern: the SpaceDescriptor carries the semantic
 *  KEY (product language); the compiler owns the mapping to engine config. Presets
 *  and inline effects share the SAME underlying model (ObjectEffects) — a preset is
 *  just a named recipe that expands to the lights/emitters you'd write by hand.
 *
 *  Used by: the Space Compiler (resolution), the ObjectInspector (preset dropdown),
 *  and the AI generator (compact semantic output). */
export const OBJECT_EFFECT_PRESETS = {
  candle: {
    lights: [
      {
        type: "point",
        color: "#ffaa44",
        intensity: 1.2,
        distance: 6,
        decay: 2,
        offset: [0, 0.3, 0],
        flicker: true,
      },
    ],
  },
  lantern: {
    lights: [
      {
        type: "point",
        color: "#ffcc77",
        intensity: 1.8,
        distance: 10,
        decay: 2,
        offset: [0, 0.5, 0],
        flicker: true,
      },
    ],
  },
  torch: {
    emitters: [
      {
        type: "fire",
        offset: [0, 0.3, 0],
        params: { radius: 0.15, height: 0.5, count: 180, intensity: 1.3 },
      },
    ],
    lights: [
      {
        type: "point",
        color: "#ff8833",
        intensity: 2.0,
        distance: 12,
        decay: 2,
        offset: [0, 0.4, 0],
        flicker: true,
      },
    ],
  },
  campfire: {
    emitters: [
      {
        type: "fire",
        offset: [0, 0.15, 0],
        params: { radius: 0.12, height: 0.35, count: 250, intensity: 2.0 },
      },
      {
        type: "fire",
        offset: [0, 0.2, 0],
        params: { radius: 0.22, height: 0.65, count: 180, intensity: 1.3 },
      },
      {
        type: "fire",
        offset: [0, 0.25, 0],
        params: { radius: 0.35, height: 0.9, count: 100, intensity: 0.7 },
      },
    ],
    lights: [
      {
        type: "point",
        color: "#ffcc66",
        intensity: 2.5,
        distance: 18,
        decay: 2,
        offset: [0, 0.4, 0],
        flicker: true,
      },
    ],
  },
  glow: {
    scaleMode: "surface",
    distanceFactor: 1.5,
    emissive: "#ffffff",
    emissiveIntensity: 0.3,
    rimGlow: { color: "#aaddff", intensity: 1.0 },
    lights: [
      {
        type: "point",
        color: "#88ccff",
        intensity: 1.5,
        distance: 8,
        decay: 1,
        offset: [0, 0.5, 0],
      },
    ],
  },
  /** Atmospheric halo for externally-lit bodies (e.g. Earth).
   *  No emissive (surface is lit by scene lights), no limb wash —
   *  only a thin halo beyond the silhouette from atmospheric scattering. */
  aura: {
    scaleMode: "surface",
    distanceFactor: 1.2,
    rimGlow: { color: "#6699ff", intensity: 0.7, surfaceGlow: false },
    lights: [
      {
        type: "point",
        color: "#99bbff",
        intensity: 0.8,
        distance: 8,
        decay: 1,
        offset: [0, 0.5, 0],
      },
    ],
  },
  spotlight: {
    lights: [
      {
        type: "spot",
        color: "#ffffff",
        intensity: 2.0,
        distance: 20,
        decay: 2,
        offset: [0, 2, 0],
      },
    ],
  },
} satisfies Record<string, ObjectEffectPresetDef>;

/** Semantic object-effect preset key — stored in SpaceObjectEffects.preset. */
export type ObjectEffectPresetKey = keyof typeof OBJECT_EFFECT_PRESETS;
